/**
 * AccountMerge — Accounts S6 (0141; rulings of 2026-10-24 and 2026-10-27):
 * the "Merge with…" confirm dialog. The merged toast with its Undo, and the
 * Undo call the survivor's page reuses, live in accountMergeActions.ts.
 *
 * A merge is never automatic: the dialog reads GET /v1/sales/accounts/
 * merge-preview first, names both accounts and the counts that will move,
 * pre-selects the server's suggested survivor (more contacts, deals and
 * activities; the older on a tie) and lets the user pick the other, and only
 * the confirm button calls POST /v1/sales/accounts/merges. Every number in the
 * dialog is the preview's — nothing is counted client-side. Undo works from
 * the merge record at any time (the toast's action, or the survivor's page),
 * and its answer says which rows had moved since and were left where they are.
 *
 * Visual (docs/design/stier/ADOPTION.md): the DeleteDealDialog shape — the
 * scrim, a .k-card panel, k-btn / k-btn-secondary — and the AccountPicker for
 * the other account (pick only, no create row). No new tokens.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { fetchAccountMergePreview, mergeAccounts } from '../../api'
import type { SalesAccountMerge, SalesAccountMergePreview, AccountMergeSide } from '../../../../api/types'
import { apiErrorMessage } from '../../../../api/client'
import { overlay } from '../../../../theme/tokens'
import { AccountPicker, type AccountPick } from './AccountPicker'
import { movedPhrase, plural } from './accountMergeActions'

export interface MergeAccountRef { id: string; name: string }

function SideOption({ side, checked, suggested, onPick }: { side: AccountMergeSide; checked: boolean; suggested: boolean; onPick: () => void }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-control border cursor-pointer ${checked ? 'border-accent' : 'border-edge-row'}`}>
      <input type="radio" name="merge-survivor" className="mt-1" checked={checked} onChange={onPick} />
      <span className="min-w-0">
        <span className="block text-13 font-semibold text-ink-primary truncate">
          Keep {side.name}{suggested && <span className="text-11 font-normal text-ink-muted"> · suggested</span>}
        </span>
        <span className="block text-11 text-ink-muted">
          {plural(side.contacts, 'contact')} · {plural(side.deals, 'deal')} · {plural(side.activities, 'activity', 'activities')}
        </span>
      </span>
    </label>
  )
}

export function AccountMergeDialog({ source, target: initialTarget, onCancel, onMerged }: {
  source: MergeAccountRef
  /** Known from a duplicate pill; absent from the ⋯ menu (the user picks). */
  target?: MergeAccountRef
  onCancel: () => void
  onMerged: (rec: SalesAccountMerge, survivorName: string) => void
}) {
  const [target, setTarget] = useState<MergeAccountRef | null>(initialTarget ?? null)
  const [pick, setPick] = useState<AccountPick>({ accountId: '', company: '' })
  const [preview, setPreview] = useState<SalesAccountMergePreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [survivorId, setSurvivorId] = useState('')
  const [merging, setMerging] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  useEffect(() => {
    if (!target) return
    let alive = true
    fetchAccountMergePreview(source.id, target.id)
      .then(p => { if (alive) { setPreview(p); setSurvivorId(p.suggestedSurvivorId) } })
      .catch(e => { if (alive) setPreviewError(apiErrorMessage(e, 'Failed to read the two accounts')) })
    return () => { alive = false }
  }, [source.id, target])

  function onPick(next: AccountPick) {
    setPick(next)
    if (!next.accountId) return
    if (next.accountId === source.id) {
      toast.error('Pick a different account to merge with')
      return
    }
    setPreview(null)
    setPreviewError(null)
    setTarget({ id: next.accountId, name: next.company })
  }

  const survivor = preview?.accounts.find(s => s.id === survivorId) ?? null
  const merged = preview?.accounts.find(s => s.id !== survivorId) ?? null

  async function confirm() {
    if (!target || !survivor || !merged) return
    setMerging(true)
    try {
      const rec = await mergeAccounts({ accountIds: [source.id, target.id], survivorId: survivor.id })
      onMerged(rec, survivor.name)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to merge the accounts'))
      setMerging(false)
    }
  }

  const inputCls = 'w-full h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: overlay.scrim }}
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div role="dialog" aria-modal="true" aria-labelledby="merge-accounts-title"
        className="k-card w-full max-w-md p-6 flex flex-col gap-4">
        <h2 id="merge-accounts-title" className="text-16 font-semibold text-ink-primary">
          {target ? `Merge ${source.name} and ${target.name}?` : `Merge ${source.name} with…`}
        </h2>

        {!target && (
          <div className="flex flex-col gap-2">
            <p className="text-13 text-ink-secondary">Pick the account it duplicates.</p>
            <AccountPicker value={pick} onChange={onPick} className={inputCls} allowCreate={false} autoFocus
              placeholder="Search accounts…" />
          </div>
        )}

        {target && !preview && !previewError && <p className="text-13 text-ink-muted">Reading both accounts…</p>}
        {previewError && <p className="text-13 text-ink-secondary">{previewError}</p>}

        {preview && survivor && merged && (
          <>
            <div role="radiogroup" aria-label="Account to keep" className="flex flex-col gap-2">
              {preview.accounts.map(side => (
                <SideOption key={side.id} side={side} checked={side.id === survivorId}
                  suggested={side.id === preview.suggestedSurvivorId} onPick={() => setSurvivorId(side.id)} />
              ))}
            </div>
            <p className="text-13 text-ink-secondary">
              {movedPhrase(merged.contacts, merged.deals)} move from {merged.name} to {survivor.name}, with their activities.
              {' '}“{merged.name}” is kept as another name for {survivor.name}. You can undo this from {survivor.name}’s page.
            </p>
          </>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="k-btn-secondary" disabled={merging} onClick={onCancel}>Cancel</button>
          <button type="button" className="k-btn disabled:opacity-60" disabled={merging || !survivor || !merged} onClick={() => { void confirm() }}>
            {merging ? 'Merging…' : 'Merge accounts'}
          </button>
        </div>
      </div>
    </div>
  )
}
